import React, { useRef, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { useDgptAlertsFlag } from '../../utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';
import { useAors, useDeleteAor } from 'hooks/useAors';
import Alert from 'react-bootstrap/Alert';
import { AddUpdateAor } from './AddUpdateAor';
import { Card, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { AreaOfResponsibility } from '../../types/aor';
import { useCreateAorsFromGeoJSON } from 'hooks/useCreateAorsFromGeoJSON';
import { useIsDev } from 'hooks/useIsDev';
import { toast } from 'react-toastify';
import { parseError } from 'utils/parseTypedErrorMessage';
import { noAutoClose } from 'assets/data/config';

/**
 * CgGeojsonUploadButton — invisible utility button for manual endpoint testing.
 *
 * Usage (browser console):
 * document.getElementById('cg-geojson-upload').click();
 */
const CgGeojsonUploadButton: React.FC = () => {
  const aorText = useAorText();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: createAorsFromGeoJSON } = useCreateAorsFromGeoJSON();

  const isDev = useIsDev();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    // Reset so the same file(s) can be re-selected if needed
    e.target.value = '';

    createAorsFromGeoJSON(files, {
      onSuccess: () => {
        toast.success('Debug upload succeeded');
      },
      onError: (error) => {
        const message = parseError(error) ?? 'Debug upload failed.';
        toast.error(message, noAutoClose);
      },
    });
  };

  return (
    <div
      style={
        isDev
          ? {}
          : { position: 'absolute', width: 0, height: 0, overflow: 'hidden' }
      }
      aria-hidden={isDev ? 'false' : 'true'}
    >
      <h5>{aorText('AOR upload')}</h5>
      <input
        ref={fileInputRef}
        id="cg-geojson-upload"
        type="file"
        multiple
        onChange={handleFileChange}
        tabIndex={-1}
      />
    </div>
  );
};

const ManageLocation: React.FC = () => {
  const { data: allAor = [] } = useAors();
  const dgptAlertsEnabled = useDgptAlertsFlag();
  const aorText = useAorText();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [aorToDelete, setAorToDelete] = useState<string | null>(null);
  const [editAorData, setEditAorData] = useState<AreaOfResponsibility | null>(
    null
  );
  const deleteAor = useDeleteAor();

  const handleEdit = (aor: AreaOfResponsibility) => {
    setEditAorData(aor);
    setShowAddDialog(true);
  };

  const handleDelete = (id: string) => {
    setAorToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (aorToDelete) {
      deleteAor.mutate(aorToDelete);
      setShowDeleteDialog(false);
      setAorToDelete(null);
    }
  };

  return (
    <div className="rounded bg-gray-800 p-4">
      {allAor.length === 0 && dgptAlertsEnabled && (
        <Alert variant="warning" className="mb-3">
          <strong>
            {aorText(
              'At least one Area of Responsibility is required for AI Alerts.'
            )}
          </strong>
        </Alert>
      )}
      <div className="d-flex align-items-center mb-4">
        <h6 className="text-uppercase m-0">
          {aorText('Configure Areas of Responsibility')}
        </h6>
      </div>
      <hr className="dashed my-3" />

      {allAor.length > 0 && (
        <div className="mb-4">
          {allAor.map((aor) => (
            <Card key={aor.id} className="mb-3 bg-gray-700">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{aor.name}</h6>
                    {aor.description && (
                      <div className="text-muted mb-0">
                        <TiptapRichTextEditor
                          value={aor.description}
                          readOnly
                          showToolbar={false}
                        />
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <StylishNewButton
                      type="button"
                      className="button--secondary button--icon"
                      onClick={() => handleEdit(aor)}
                    >
                      <FaEdit />
                    </StylishNewButton>
                    <StylishNewButton
                      type="button"
                      className="button--secondary button--icon"
                      onClick={() => handleDelete(aor.id)}
                    >
                      <FaTrash />
                    </StylishNewButton>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <div className="button-group justify-content-end">
        <StylishNewButton
          type="button"
          className="button--primary w-md-auto w-100"
          onClick={() => {
            setEditAorData(null);
            setShowAddDialog(true);
          }}
        >
          {`Add Item`}
        </StylishNewButton>
      </div>

      <AddUpdateAor
        show={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setEditAorData(null);
        }}
        mode={editAorData ? 'Edit' : 'Add'}
        editAorData={editAorData}
      />

      <Modal
        show={showDeleteDialog}
        onHide={() => {
          setShowDeleteDialog(false);
          setAorToDelete(null);
        }}
        centered
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {aorText(
            'Are you sure you want to delete this Area of Responsibility?'
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              type="button"
              className="button--secondary button--reverse"
              onClick={() => {
                setShowDeleteDialog(false);
                setAorToDelete(null);
              }}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              type="button"
              className="button--primary"
              onClick={confirmDelete}
            >
              Delete
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </Modal>
      <CgGeojsonUploadButton />
    </div>
  );
};

export default ManageLocation;
